import React, { useEffect } from 'react';
import * as useAPI from 'hooks/useAPI';
import * as consts from 'config/consts';
import Loading from 'components/commons/loading/Loading';
import NumberLabel from 'components/features/utils/NumberLabel';
import { formatDate } from 'utils/formatter';
import { useGetTransactions } from 'hooks/useGetTransactions';
import { TransactionItem } from './TransactionItem';
import { sortByNumber } from './AssetList';

const DepositsAndWithdrawalsHistoryLoading = () => {
  return (
    <>
      <div className="pt-4 font-bold">--月--日の入出金</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
      <div className="text-xs mt-4 text-bluegray-500">
        ※入出金記録はMoneytree連携の口座が対象となります。みんかぶ連携口座の入出金記録は表示されません。
      </div>
    </>
  );
};

const DepositsAndWithdrawalsHistory = ({
  targetDate,
  onAccountItemClick,
}: {
  targetDate: Date | null;
  onAccountItemClick: (account: string) => void;
}) => {
  const { transactions: transactions_, getTransactions } = useGetTransactions();

  useEffect(() => {
    getTransactions({ reportType: consts.reportType.日次 });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (useAPI.isLoading(transactions_))
    return <DepositsAndWithdrawalsHistoryLoading />;

  const transactions = transactions_.transactions.items;
  transactions.sort((first, second) =>
    sortByNumber(first.amount, second.amount)
  );

  return (
    <>
      <div className="pt-4 font-bold">
        {targetDate && formatDate(targetDate, 'M月d日') + 'の入出金'}
      </div>
      <div className="border rounded-lg mt-2 overflow-hidden">
        <div className="bg-bluegray-50">
          <div className="flex mx-3 items-center">
            <div className="py-4 text-sm font-bold text-bluegray-500">
              入出金の合計
            </div>
            <div className="flex-grow flex justify-end">
              <NumberLabel
                value={transactions.reduce(function(sum, transaction) {
                  return sum + transaction.amount;
                }, 0)}
                unitName="円"
                className="font-bold"
                classNames={['text-xl', 'text-xs mt-1 ml-1']}
              />
            </div>
          </div>
        </div>
        <div className="border-t">
          {transactions.length ? (
            transactions.map((transaction, i) => {
              return (
                <TransactionItem
                  {...{
                    borderTop: i > 0 && <div className="border-b ml-3" />,
                    onAccountItemClick,
                    transaction,
                    key: i,
                  }}
                />
              );
            })
          ) : (
            <div className="flex items-center justify-center mx-3 py-4 text-sm text-bluegray-500">
              {targetDate &&
                formatDate(targetDate, 'M月d日') +
                  'に取得した出入金記録はありません'}
            </div>
          )}
        </div>
      </div>
      <div className="text-xs mt-4 text-bluegray-500">
        ※入出金記録はMoneytree連携の口座が対象となります。みんかぶ連携口座の入出金記録は表示されません。
      </div>
    </>
  );
};

type TransactionsHistoryProps = {
  onAccountItemClick: (account: string) => void;
  targetDate: Date | null;
};

export const DepositAndWithdrawalHistory = ({
  onAccountItemClick,
  targetDate,
}: TransactionsHistoryProps): React.ReactElement => {
  return (
    <>
      <div className="text-lg font-bold ml-4 mt-16 mb-1">入出金の履歴</div>
      <div className="bg-white">
        <div className="m-4 pb-8">
          <DepositsAndWithdrawalsHistory
            {...{ targetDate, onAccountItemClick }}
          />
        </div>
      </div>
    </>
  );
};
